import { sanitizeTriage, type ClashTriageMap } from './clashTriage'
import {
  CLASH_TRIAGE_KEY,
  corridasVazias,
  sanitizeCorridas,
  sanitizeSetup,
  type ClashSetupFile,
} from './clashTriageStorage'
import type { ClashCorrida, ClashEnvioModulo } from './clashReconciliacao'
import type { ClashUltimaExportacao } from './clashExportacaoRegisto'
import {
  lerClassificacao,
  temClassificacao,
  type ClashClassificacaoFile,
} from './clashAgrupamentoEstado'

// ---------------------------------------------------------------------------
// A deteccao de conflitos no ficheiro de sessao (.vn2r-session, v7+).
//
// Vai tudo o que permite repetir a corrida noutra maquina e reconciliar
// com o que estava decidido: os conjuntos A e B e a configuracao, a
// triagem toda (com os grupos da proposta e os eventos das corridas), o
// historico das corridas, o identificador do projecto (Guids BCF
// estaveis), as chaves da ultima corrida e os topicos enviados para o
// modulo BCF e o registo da ultima exportacao para ficheiro (campo
// opcional: as sessoes v7 gravadas antes dele leem-se sem registo). Os
// RESULTADOS nao vao: sao derivados dos modelos e
// repete-se a corrida.
//
// As sessoes anteriores (sem a seccao) traziam a triagem dentro do
// `local_work`, na chave `vn2r.clash.triage`; le-se dai, com o mesmo
// formato.
// ---------------------------------------------------------------------------

export const VERSAO_SECCAO_CONFLITOS = 1

export interface VnSessionConflitos {
  versao: number
  setup: ClashSetupFile | null
  triagem: ClashTriageMap
  historico: ClashCorrida[]
  projectoBcf: string | null
  chavesUltimaCorrida: string[]
  modulo: ClashEnvioModulo | null
  /** Registo da ultima exportacao para ficheiro. Ausente nas sessoes
   *  anteriores a "So o que mudou desde a ultima exportacao". */
  ultimaExportacao?: ClashUltimaExportacao | null
  /** Regras, especialidades, metodo de agrupamento e grupos. Ausente nas
   *  sessoes anteriores a classificacao automatica. */
  classificacao?: ClashClassificacaoFile | null
  /** A triagem partilhada no workspace em que a sessao foi gravada (para
   *  quem a abrir poder entrar nela). Ausente nas sessoes anteriores e
   *  nas gravadas sem partilha. */
  partilha?: { projetoId: string; codigo: string } | null
}

function lerPartilha(v: unknown): { projetoId: string; codigo: string } | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  if (typeof o.projetoId !== 'string' || !/^[0-9a-fA-F-]{36}$/.test(o.projetoId)) return null
  if (typeof o.codigo !== 'string' || !o.codigo.trim()) return null
  return { projetoId: o.projetoId, codigo: o.codigo.slice(0, 60) }
}

/** A seccao a gravar, ou null quando nao ha nada de conflitos. */
export function construirSeccaoConflitos(o: {
  setup: ClashSetupFile | null
  triagem: ClashTriageMap
  historico: ClashCorrida[]
  projectoBcf: string | null
  chavesUltimaCorrida: string[]
  modulo: ClashEnvioModulo | null
  ultimaExportacao?: ClashUltimaExportacao | null
  classificacao?: ClashClassificacaoFile | null
  partilha?: { projetoId: string; codigo: string } | null
}): VnSessionConflitos | null {
  const comClassificacao = !!o.classificacao && temClassificacao(o.classificacao)
  const vazia =
    Object.keys(o.triagem).length === 0 &&
    o.historico.length === 0 &&
    !o.modulo &&
    !o.ultimaExportacao &&
    !o.setup &&
    !comClassificacao &&
    !o.partilha
  if (vazia) return null
  const { classificacao, ultimaExportacao, partilha, ...resto } = o
  return {
    versao: VERSAO_SECCAO_CONFLITOS,
    ...resto,
    ...(ultimaExportacao ? { ultimaExportacao } : {}),
    ...(comClassificacao ? { classificacao } : {}),
    ...(partilha ? { partilha } : {}),
  }
}

/** Le a seccao de um ficheiro de sessao. Entrada estragada le-se campo
 *  a campo, como a triagem do localStorage. */
export function lerSeccaoConflitos(v: unknown): VnSessionConflitos | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  const corridas = sanitizeCorridas(o)
  const partilha = lerPartilha(o.partilha)
  return {
    versao: typeof o.versao === 'number' ? o.versao : VERSAO_SECCAO_CONFLITOS,
    setup: sanitizeSetup(o.setup),
    triagem: sanitizeTriage(o.triagem),
    ...corridas,
    ...(o.classificacao ? { classificacao: lerClassificacao(o.classificacao) } : {}),
    ...(partilha ? { partilha } : {}),
  }
}

/** Sessoes antigas: a triagem vinha no `local_work`. */
export function seccaoDoLocalWork(
  localWork: readonly { key: string; raw: string }[] | undefined,
): VnSessionConflitos | null {
  const item = (localWork ?? []).find((w) => w.key === CLASH_TRIAGE_KEY)
  if (!item) return null
  let file: Record<string, unknown>
  try {
    file = JSON.parse(item.raw) as Record<string, unknown>
  } catch {
    return null
  }
  if (!file || typeof file !== 'object') return null
  return {
    versao: VERSAO_SECCAO_CONFLITOS,
    setup: sanitizeSetup(file.setup),
    triagem: sanitizeTriage(file.entries),
    ...sanitizeCorridas(file),
    ...(file.classificacao ? { classificacao: lerClassificacao(file.classificacao) } : {}),
  }
}

function canonico(t: ClashTriageMap): string {
  return JSON.stringify(
    Object.keys(t)
      .sort()
      .map((k) => [k, t[k]]),
  )
}

/** As duas triagens dizem coisas diferentes? (A ordem das chaves nao
 *  conta.) */
export function triagensDiferentes(a: ClashTriageMap, b: ClashTriageMap): boolean {
  return canonico(a) !== canonico(b)
}

export { corridasVazias }
